/**
 * @file homeCards.tsx
 * @brief The four Home cards, as the phone draws them.
 *
 * @details
 * Anatomy, strings and tab rules are the phone's. Since 07-10-2026 (DES)
 * meetings, sessions and notes carry their title (15 bold, the date's grey),
 * the date and a hairline INSIDE the card (`RailCard headInside`), with the
 * duration on the title's line:
 * - MeetingCard: RecentMeetings.tsx. Avatar stack (3 + "+N"), 👥 count,
 *   output languages as codes ("EN · UR"), the status tab.
 * - SessionCard: the phone's SessionCard with `flagsOnly`: Person B + date +
 *   PAID, then the language pair as flags (no faces) and the `#` / pen
 *   buttons that open the tag and comment dropdowns (SessionTagsMenu /
 *   SessionCommentMenu, 04-10-2026); billing / HAS NOTES tabs.
 * - NoteTimelineItem: the Notes list's row (the phone's Home lists notes
 *   the same way), laid on its side for the desktop's swipe rail.
 * - ScheduledCard: HorizontalSessionCard.tsx. There is no Join on the web,
 *   because interpretation runs on the phone.
 *
 * @author DES
 * @date 30-09-2026
 */

import { failureBadgeLabel, failureReasonOf } from "@/features/company-meetings/meetingFailure";
import { PersonPhoto } from "@/components/ui/PersonPhoto";
import { format } from "date-fns";
import type { CSSProperties } from "react";
import {
  ArrowRight,
  CalendarDays,
  Pin,
  Sparkles,
  Tag,
  Briefcase,
  Clock,
  Hash,
  Mic,
  PenLine,
  Radio,
  StickyNote,
  User,
  Users,
  UsersRound,
} from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { cn } from "@/shared/theme/cn";
import { isNotesTierMeeting, type MeetingRow } from "@/features/company-meetings/schemas";
import { MeetingSharingLine } from "@/features/employee-boardroom/MeetingSharingLine";
import { MeetingCardShareMenu } from "@/features/employee-boardroom/MeetingShareItems";
import {
  formatWhen as noteWhen,
  noteKindLine,
  noteIsAgenda,
  noteMeetingLabel,
  notePreview,
} from "@/features/notes/noteFormat";
import { isRTLText } from "@/shared/utils/formatters";
import { Link } from "react-router-dom";
import { MeetingOwnerLine } from "@/features/company-meetings/components/MineTeamSwitch";
import { CardTab, RailCard } from "./homeRail";
import { FlipClock } from "@/features/employee-boardroom/FlipClock";
import { useCountdown } from "@/shared/utils/countdown";
import { formatSessionDuration } from "@/shared/utils/duration";
import { SessionTagsMenu } from "@/features/sessions/SessionTagsMenu";
import { SessionCommentMenu } from "@/features/sessions/SessionCommentMenu";
import type { HomeNote, HomeScheduled, HomeSession } from "./homeApi";

/** Phone card widths: 65% of a 375pt screen, rounded up for a desktop. */
export const CARD_W = 264;
export const SCHEDULED_W = 320;

/** `RailCard headInside` (DES 07-10-2026): 16 padding, the title line (22),
 *  2 + the date line (18), 12 + the 1px hairline. Each card adds its row
 *  (8 above, 16 below). */
const HEAD_IN_H = 16 + 22 + 2 + 18 + 12 + 1;
/** Meetings: the avatars + count + codes row, 26px. */
export const MEETING_BODY_H = HEAD_IN_H + 8 + 26 + 16;

/** "🕐 17m": every card's duration, on the title's line. */
function Duration({ seconds }: { seconds?: number | null }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      <Clock className="h-3.5 w-3.5 text-app-text-secondary" strokeWidth={1.5} />
      <span className="text-[13px] font-semibold text-app-text-secondary">
        {formatSessionDuration(seconds)}
      </span>
    </span>
  );
}

/** "en-GB" → "EN". */
const langShort = (id: string) => id.split(/[-_]/)[0].toUpperCase();

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ----------------------------------------------------------------------
// Meeting
// ----------------------------------------------------------------------
const MEETING_STATUS: Record<string, { label: string; dot: string; text: string }> = {
  ready: { label: "Ready", dot: "bg-app-success", text: "text-app-success" },
  error: { label: "Failed", dot: "bg-destructive", text: "text-destructive" },
  uploading: { label: "Uploading", dot: "bg-brand", text: "text-brand" },
  synthesis_processing: { label: "Synthesizing", dot: "bg-brand", text: "text-brand" },
  resolved_pending: { label: "Resolve speakers", dot: "bg-brand", text: "text-brand" },
};

type Person = { name: string; image?: string | null; external?: boolean };

function meetingPeople(m: MeetingRow): Person[] {
  const src = (m.attendees?.length ? m.attendees : m.participants) ?? [];
  const out: Person[] = [];
  const seen = new Set<string>();
  for (const p of src) {
    const name = (p.name ?? "").trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push({ name, image: p.profileImageUrl, external: p.isExternal });
  }
  return out;
}

function AvatarStack({ people }: { people: Person[] }) {
  // At most three circles, "+N" included (DES 04-10-2026).
  const shown = people.slice(0, people.length > 3 ? 2 : 3);
  const extra = people.length - shown.length;
  return (
    <div className="flex">
      {shown.map((p, i) => (
        <span
          key={p.name}
          title={p.name}
          className={cn(
            "flex h-[26px] w-[26px] items-center justify-center overflow-hidden rounded-full border-2 border-app-card text-[10px] font-bold text-app-text",
            p.external ? "bg-brand/10" : "bg-app-quaternary",
            i > 0 && "-ml-2",
          )}
        >
          <PersonPhoto src={p.image} fallback={initials(p.name)} />
        </span>
      ))}
      {extra > 0 ? (
        <span className="-ml-2 flex h-[26px] w-[26px] items-center justify-center rounded-full border-2 border-app-card bg-app-quaternary-dark text-[10px] font-bold text-app-text">
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

/** [09-10-2026 TRIAL] The old Home meeting card: faces, the duration by the
 *  title, language codes bottom right. false = no faces, no codes, the
 *  duration bottom right (phone: RecentMeetings.tsx). */
const MEETING_CARD_FACES = false;

export function MeetingCard({
  meeting,
  to,
  state,
  showOwner = false,
}: {
  meeting: MeetingRow;
  to: string;
  /** Router state for the opened page, e.g. where its Back returns. */
  state?: unknown;
  /** The admin's Team rail (22-10-2026): "Ali's meeting" in place of the
   *  participant count, so the card keeps its height. */
  showOwner?: boolean;
}) {
  const people = meetingPeople(meeting);
  const baseStatus = MEETING_STATUS[meeting.status] ?? {
    label: "Processing",
    dot: "bg-brand",
    text: "text-brand",
  };
  // "No speech" / "Timed out" for a stuck meeting the server stopped (18-10-2026).
  const reason = failureReasonOf(meeting as { status?: string | null; failure_reason?: unknown });
  const status = reason && reason !== "processing" ? { ...baseStatus, label: failureBadgeLabel(reason) } : baseStatus;
  const codes = Array.from(
    new Set((meeting.target_languages ?? []).filter(Boolean).map(langShort)),
  );
  return (
    <RailCard
      width={CARD_W}
      title={meeting.title?.trim() || "Untitled Meeting"}
      date={formatWhen(meeting.started_at)}
      bodyHeight={MEETING_BODY_H}
      headInside
      // TRIAL (DES 09-10-2026): the duration moves to the bottom row, where
      // the language codes were, so the title gets the full width.
      headRight={
        <>
          {MEETING_CARD_FACES ? <Duration seconds={meeting.total_duration_seconds} /> : null}
          {/* Share… / Who has access (DES 22-10-2026), on hover. */}
          <MeetingCardShareMenu
            meetingId={meeting.id}
            sharing={meeting.sharing}
            title={meeting.title?.trim() || "Untitled Meeting"}
            className="-my-1 -mr-1.5"
          />
        </>
      }
      to={to}
      state={state}
      tabs={
        <>
          {/* A Meeting Notes recording someone shared (sharing v2, DES
              21-10-2026): the default list carries them since then. */}
          {isNotesTierMeeting(meeting) ? <CardTab tone="card">Notes</CardTab> : null}
          <CardTab tone="card" dot={status.dot} className={status.text}>
            {status.label}
          </CardTab>
        </>
      }
    >
      {/* Avatars, the count as icon + number, then the output languages as
          codes (DES 07-10-2026, as the phone: codes here, flags on the
          interpretation cards). Was "N participants" + a flag stack. */}
      <div className="mt-2 flex h-[26px] items-center gap-1.5 px-4">
        {/* TRIAL (DES 09-10-2026): no faces on the Home meeting cards; the
            👥 count says how many. Flip MEETING_CARD_FACES back to show them. */}
        {!MEETING_CARD_FACES ? null : people.length ? (
          <AvatarStack people={people} />
        ) : (
          <span className="flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-app-capsule">
            <Mic className="h-3.5 w-3.5 text-app-text-secondary" strokeWidth={1.5} />
          </span>
        )}
        {showOwner && meeting.owner ? (
          <MeetingOwnerLine owner={meeting.owner} className="max-w-[55%] shrink-0" />
        ) : (
        <span
          className="flex shrink-0 items-center gap-1"
          aria-label={`${people.length} participant${people.length === 1 ? "" : "s"}`}
          title={`${people.length} participant${people.length === 1 ? "" : "s"}`}
        >
          <Users className="h-3.5 w-3.5 text-app-text-secondary" strokeWidth={1.5} />
          <span className="text-[13px] font-semibold text-app-text-secondary">{people.length}</span>
        </span>
        )}
        {/* Who has it (sharing v2, DES 21-10-2026), on the same row so the
            card keeps its height; opens "Who has access". */}
        <MeetingSharingLine
          meetingId={meeting.id}
          sharing={meeting.sharing}
          className="ml-1.5 min-w-0 flex-1"
        />
        {!MEETING_CARD_FACES ? (
          <span className="ml-auto">
            <Duration seconds={meeting.total_duration_seconds} />
          </span>
        ) : codes.length ? (
          <span className="ml-auto truncate text-[13px] font-semibold text-app-text-secondary">
            {codes.slice(0, 3).join(" · ")}
            {codes.length > 3 ? ` +${codes.length - 3}` : ""}
          </span>
        ) : null}
      </div>
    </RailCard>
  );
}

// ----------------------------------------------------------------------
// Interpretation session
// ----------------------------------------------------------------------
/** A language as its flag only; the "EN" / "UR" code beside it went
 *  (DES 04-10-2026). */
function LangCode({ code }: { code?: string | null }) {
  if (!code) return null;
  return <LanguageFlag language={{ code: code.split("-")[0], locale: code }} size="sm" />;
}

/* The countdown lives in shared/utils/countdown.ts, shared with the Diary. */

/** Every Home card's date line (meetings, sessions, notes: DES 04-10-2026,
 *  "match the sessions section").
 *  The phone's `formatWhen` ("04 Oct at 10:04" on a UK device): day and
 *  short month, then the time, in the viewer's locale. Built from two parts
 *  because browsers join a single toLocaleString with "," where iOS says
 *  "at". */
function formatWhen(iso?: string | null): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${day} at ${time}`;
}

/** Locale first (en-GB, ur-PK…), else the language: `sessionLanguageRecentId`. */
function sessionLangId(s: HomeSession, side: "source" | "target"): string | null {
  const id = (
    (side === "source"
      ? s.source_locale || s.source_language
      : s.target_locale || s.target_language) || ""
  ).trim();
  return id && id !== "undefined" ? id : null;
}

/** The card's fill, shared by the avatars' and flags' 2px rings so an
 *  overlap reads (mobile `ringColor`). Red for a stale active session wins
 *  over blue for someone else's, as on the phone. Solid colours: a ring in a
 *  translucent tint would let the face under the flag show through. */
const FILL_DEFAULT = "hsl(var(--app-card))";
const FILL_ACTIVE = "hsl(var(--brand-soft))";
const FILL_OTHERS = "color-mix(in srgb, hsl(var(--info)) 10%, hsl(var(--app-card)))";

/** Tab look for a clickable tab; `CardTab` is a span. */
const TAB_CLS =
  "inline-flex items-center gap-1 rounded-full px-2.5 py-[3px] font-bold shadow-[0_1px_2px_rgba(0,0,0,0.1)]";

/** Body of the session card: the head inside (`HEAD_IN_H`), then 8 + the
 *  flags + buttons row (24) + 16. The "For …" row adds 24 (16 + 8). */
export const SESSION_BODY_H = HEAD_IN_H + 8 + 24 + 16;
const FOR_ROW_H = 16 + 8;

/**
 * The phone's Home session card (`SessionCard` with `title="personB"` and
 * the Tag / Note buttons, FRONTEND/src/shared/components/SessionCard,
 * 04-10-2026): Person B above the card with the date and a PAID pill; the
 * billing tab and a HAS NOTES tab on the edge (REALTIME / MEDICAL on the
 * left); inside, each person's face with their language's flag, an arrow,
 * and the duration; `#` and pen buttons bottom-right; tags under the card.
 * A stale ACTIVE session turns the card red, someone else's turns it blue.
 * The buttons open the editors without opening the session.
 */
export function SessionCard({
  session,
  to,
  currentUserEmail,
}: {
  session: HomeSession;
  to?: string;
  currentUserEmail?: string | null;
}) {
  const isActive = String(session.status ?? "").toLowerCase() === "active";
  const isCorporate = session.billing_context === "corporate";
  const isOthers = Boolean(
    currentUserEmail &&
    session.participant_email &&
    session.participant_email.toLowerCase() !== currentUserEmail.toLowerCase(),
  );
  const participantName =
    [session.participant_first_name, session.participant_last_name].filter(Boolean).join(" ") ||
    session.participant_email ||
    "";
  const bName = session.person_b_name || "Person B";
  const note = (session.notes ?? "").trim();
  const tags = (session.tags ?? []).filter(Boolean);
  const fill = isActive ? FILL_ACTIVE : isOthers ? FILL_OTHERS : FILL_DEFAULT;

  /** Buttons inside the card's link: act here, never navigate. */
  return (
    <RailCard
      width={CARD_W}
      title={bName}
      date={formatWhen(session.started_at)}
      // PAID only when Stripe captured money, never inferred from the bill.
      dateExtra={
        session.is_paid ? (
          <span className="shrink-0 rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-bold tracking-[0.5px] text-success">
            PAID
          </span>
        ) : null
      }
      to={to}
      bodyHeight={SESSION_BODY_H + (isOthers ? FOR_ROW_H : 0)}
      headInside
      headRight={<Duration seconds={session.duration_seconds} />}
      bodyStyle={{ "--session-fill": fill, background: "var(--session-fill)" } as CSSProperties}
      leftTabs={
        session.session_kind === "realtime" ? (
          <span className={cn(TAB_CLS, "bg-brand-soft text-[9px] tracking-[0.8px] text-brand")}>
            <Radio className="h-2.5 w-2.5" strokeWidth={2} />
            REALTIME
          </span>
        ) : null
        // No MEDICAL pill: an icon beside the billing one (DES 04-10-2026).
      }
      tabs={
        <>
          {/* No billing pill (DES 04-10-2026): it is an icon beside # and the
              pen now. */}
          {note ? (
            // Opens the comments dropdown, as the pen does (DES 04-10-2026).
            <SessionCommentMenu
              sessionId={session.session_id}
              notes={session.notes}
              anchor={
                <button
                  type="button"
                  aria-label="Open comments"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className={cn(
                    TAB_CLS,
                    "bg-app-capsule text-[10px] tracking-[0.5px] text-app-text-secondary",
                  )}
                >
                  <StickyNote className="h-2.5 w-2.5" strokeWidth={2} />
                  HAS NOTES
                </button>
              }
            />
          ) : null}
        </>
      }
      below={
        tags.length ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 text-[13px] font-medium text-app-text-secondary">
            {tags.slice(0, 3).map((t) => (
              <span key={t} className="max-w-full truncate">
                <span className="font-bold">#</span>
                {t}
              </span>
            ))}
            {tags.length > 3 ? <span>+{tags.length - 3}</span> : null}
          </div>
        ) : null
      }
    >
      <div className="flex flex-col px-4 pt-2">
        {isOthers ? (
          <div className="mb-2 flex h-4 items-center gap-1">
            <UsersRound className="h-4 w-4 shrink-0 text-brand" strokeWidth={1.5} />
            <span className="truncate text-xs font-medium text-brand">For {participantName}</span>
          </div>
        ) : null}
        {/* The language pair as flags, no faces (DES 07-10-2026: A is always
            the user, B never has a photo, B's name is the title), then the
            buttons. Was faces + flags + the duration on their own row. */}
        <div className="flex h-6 items-center gap-2">
          <span
            className="mr-auto flex items-center gap-1.5"
            aria-label={`${session.source_language || "unknown"} to ${session.target_language || "unknown"}`}
          >
            <LanguageFlag
              language={{
                code: (sessionLangId(session, "source") ?? "").split("-")[0],
                locale: sessionLangId(session, "source") ?? undefined,
              }}
              size="sm"
              className="h-5 w-5 border-0"
            />
            <ArrowRight
              className="h-3.5 w-3.5 shrink-0 text-app-text-secondary"
              strokeWidth={1.5}
            />
            <LanguageFlag
              language={{
                code: (sessionLangId(session, "target") ?? "").split("-")[0],
                locale: sessionLangId(session, "target") ?? undefined,
              }}
              size="sm"
              className="h-5 w-5 border-0"
            />
          </span>
          {/* Tags as a dropdown under # (DES 04-10-2026), not a dialog. The
              click is stopped so the card's link doesn't open; the menu
              opens on pointer-down. */}
          <SessionTagsMenu
            sessionId={session.session_id}
            tags={tags}
            align="end"
            anchor={
              <button
                type="button"
                aria-label="Edit tags"
                title="Edit tags"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="relative rounded-full p-1 hover:bg-app-text/5"
              >
                <Hash
                  className={cn("h-4 w-4", tags.length ? "text-brand" : "text-app-text-secondary")}
                  strokeWidth={1.5}
                />
                {tags.length ? (
                  <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full border border-white bg-brand" />
                ) : null}
              </button>
            }
          />
          <span className="h-4 w-px bg-border" />
          {/* Comments as a dropdown under the pen (DES 04-10-2026). */}
          <SessionCommentMenu
            sessionId={session.session_id}
            notes={session.notes}
            anchor={
              <button
                type="button"
                aria-label="Comments"
                title="Comments"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="relative rounded-full p-1 hover:bg-app-text/5"
              >
                <PenLine
                  className={cn("h-4 w-4", note ? "text-brand" : "text-app-text-secondary")}
                  strokeWidth={1.5}
                />
                {note ? (
                  <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full border border-white bg-brand" />
                ) : null}
              </button>
            }
          />
          <span className="h-4 w-px bg-border" />
          {/* A medical session: the Assessments icon, just before the billing
              one; it replaces the MEDICAL pill (DES 04-10-2026). */}
          {session.session_kind === "medical_assessment" ? (
            <>
              <span
                className="flex h-6 w-6 items-center justify-center"
                role="img"
                aria-label="Medical assessment"
                title="Medical"
              >
                {/* DES 08-10-2026: app icon, was FileHeart */}
                <MedicalReport className="h-4 w-4 text-brand" strokeWidth={1.5} />
              </span>
              <span className="h-4 w-px bg-border" />
            </>
          ) : null}
          {/* Last (DES 04-10-2026: after the tag and comment). Billing as an icon only: briefcase = corporate (in its blue),
              person = personal. Not a button. */}
          <span
            className="flex h-6 w-6 items-center justify-center"
            role="img"
            aria-label={isCorporate ? "Corporate session" : "Personal session"}
            title={isCorporate ? "Corporate" : "Personal"}
          >
            {isCorporate ? (
              <Briefcase className="h-4 w-4 text-info" strokeWidth={1.5} />
            ) : (
              <User className="h-4 w-4 text-app-text-secondary" strokeWidth={1.5} />
            )}
          </span>
        </div>
      </div>
    </RailCard>
  );
}

// ----------------------------------------------------------------------
// Note: the Notes list's row, laid on its side
// ----------------------------------------------------------------------
/** Node centre from the item's top: the rail sits above the text. */
const NOTE_NODE_PX = 8;

/**
 * One note as the Notes list draws it (NotesPage `NoteRow`, the phone's
 * `NoteRow`): title, date, the Manual / Generated line, the meeting link,
 * tags and a two-line preview. Same words and type sizes. Only the rail
 * turns (DES 07-10-2026, "same as mobile but horizontal"): the node sits
 * above the text and the line runs right to the next note, across the
 * rail's 12px gap; it stops at the first and last note, as in a section.
 */
export function NoteTimelineItem({
  note,
  to,
  first,
  last,
}: {
  note: HomeNote;
  to: string;
  first: boolean;
  last: boolean;
}) {
  const shared = note.is_owner === false;
  const typed = note.source === "typed";
  const title = note.title?.trim() || "Untitled note";
  const preview = notePreview(note);
  const meeting = noteMeetingLabel(note);
  const tags = note.tags ?? [];
  const KindIcon = typed ? PenLine : Sparkles;
  return (
    <Link to={to} className="group block shrink-0 snap-start text-left" style={{ width: CARD_W }}>
      <div className="relative h-4" aria-hidden>
        {!first ? (
          <span
            className="absolute left-0 h-0.5 -translate-y-1/2 bg-border"
            style={{ top: NOTE_NODE_PX, width: 8 }}
          />
        ) : null}
        {!last ? (
          <span
            className="absolute left-2 h-0.5 -translate-y-1/2 bg-border"
            style={{ top: NOTE_NODE_PX, right: -12 }}
          />
        ) : null}
        <span
          className={cn(
            "absolute left-0 h-4 w-4 rounded-full border-[3px] border-app-page",
            shared || typed ? "bg-muted-foreground/50" : "bg-brand",
          )}
          style={{ top: 0 }}
        />
      </div>
      <div className="pr-3 pt-3 transition-opacity group-active:opacity-60">
        <span className="flex min-w-0 items-start gap-1.5">
          <span
            dir={isRTLText(title) ? "rtl" : "ltr"}
            className="line-clamp-2 text-xl font-bold leading-7 text-foreground"
          >
            {title}
          </span>
          {note.is_pinned ? (
            <Pin className="mt-1.5 h-4 w-4 shrink-0 text-brand" strokeWidth={1.5} />
          ) : null}
        </span>
        <span className="mt-1 block text-[15px] font-semibold text-foreground">
          {noteWhen(shared ? note.shared_at || note.created_at : note.created_at)}
        </span>
        {shared && note.shared_by_name ? (
          <span className="mt-1 flex items-center gap-1.5 text-[15px] text-brand">
            <Users className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            <span className="truncate">Shared by {note.shared_by_name}</span>
          </span>
        ) : null}
        <span className="mt-1 flex items-center gap-1.5 text-[15px] text-muted-foreground">
          <KindIcon
            className={cn("h-[15px] w-[15px] shrink-0", typed ? "" : "text-brand")}
            strokeWidth={1.5}
          />
          <span className="truncate">{noteKindLine(note)}</span>
        </span>
        {meeting ? (
          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[15px] text-brand">
            {/* DES 08-10-2026: app icon for an agenda, was CalendarDays */}
            {noteIsAgenda(note) ? (
              <AgendaFlag className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            ) : (
              <CalendarDays className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            )}
            <span className="truncate">{meeting}</span>
          </span>
        ) : null}
        {tags.length ? (
          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[15px] text-muted-foreground">
            <Tag className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            <span className="truncate">{tags.join(" · ")}</span>
          </span>
        ) : null}
        {preview ? (
          <span
            dir={isRTLText(preview) ? "rtl" : "ltr"}
            className="mt-1 line-clamp-2 text-[15px] leading-[22px] text-muted-foreground"
          >
            {preview}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

// ----------------------------------------------------------------------
// Scheduled interpretation booking
// ----------------------------------------------------------------------
export function ScheduledCard({ row, to }: { row: HomeScheduled; to?: string }) {
  const at = row.scheduled_at ? new Date(row.scheduled_at) : null;
  const when = at && !Number.isNaN(at.getTime()) ? format(at, "EEE, d MMM · h:mm a") : "—";
  const booked = row.created_at ? format(new Date(row.created_at), "d MMM, h:mm a") : "";
  const late = row.time_status === "late";
  const st = String(row.status ?? "").toLowerCase();
  const timeLeft = useCountdown(
    row.scheduled_at,
    !["completed", "attended", "cancelled"].includes(st),
    true,
  );
  return (
    <RailCard
      width={SCHEDULED_W}
      to={to}
      title={row.person_b_name?.trim() || "Guest"}
      date={when}
      bodyHeight={96}
      bodyClassName={late ? "ring-[1.5px] ring-brand" : undefined}
      tabs={
        <CardTab
          tone="card"
          dot={late ? "bg-brand" : "bg-app-text-secondary"}
          className={late ? "text-brand" : ""}
        >
          {late ? "Active Now" : "Scheduled"}
        </CardTab>
      }
    >
      <div className="flex h-full flex-col justify-between px-3 pb-2 pt-5">
        <div className="flex items-center gap-1.5">
          <User className="h-3 w-3 text-app-text-secondary" strokeWidth={1.5} />
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-app-text-secondary">
            {row.participant_first_name || "Employee"}
          </span>
          <LangCode code={row.source_language} />
          <ArrowRight className="h-2.5 w-2.5 text-app-text-secondary" />
          <LangCode code={row.target_language} />
        </div>
        <div className="flex items-end gap-2">
          <p className="line-clamp-2 min-w-0 flex-1 whitespace-pre-line text-[10px] font-medium leading-[13px] text-app-text-secondary/80">
            {`Booked by ${[row.scheduler_first_name, row.scheduler_last_name].filter(Boolean).join(" ") || "—"}\n${booked}`}
          </p>
          {/* The countdown inside the card, as the other cards' time (DES
              04-10-2026), grey like their time: the flip draws the eye. */}
          {timeLeft ? (
            <span className="flex shrink-0 items-center gap-1 text-app-text-secondary">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
              {/* Rolls like the interpretation timer (DES 04-10-2026). */}
              <FlipClock
                value={timeLeft}
                fontSize={13}
                direction="down"
                inheritFont
                className="font-semibold"
              />
            </span>
          ) : null}
        </div>
      </div>
    </RailCard>
  );
}
